import type { Page } from '../../types.js';

/**
 * The journey page. Two sequences carry it — the trade itself as a `ladder`, and
 * what settlement writes as a `flow` — because both are ordered and a reader wants
 * to know where they are in them. The closed-market section is separate on purpose:
 * it is the behaviour most pilots never see and every support desk meets.
 */
const page: Page = {
  slug: 'how-it-works',
  title: 'How a trade actually happens',
  lede:
    'From a customer tapping buy to metal recorded in their name, with every number the platform keeps along the way. No code on this page.',
  group: 'Start here',
  blocks: [
    {
      kind: 'stats',
      items: [
        { value: '30s', label: 'Default quote window', sub: 'Configurable per bank', accent: 'amber' },
        { value: '5', label: 'Open quotes per customer', sub: 'The default cap', accent: 'amber' },
        { value: '6dp', label: 'Rate precision', sub: 'Per gram, held as exact decimals', accent: 'teal' },
        { value: '1µg', label: 'Smallest unit of metal', sub: 'Weights are whole micrograms', accent: 'teal' },
      ],
    },

    { kind: 'heading', text: 'The trade, step by step' },
    {
      kind: 'ladder',
      steps: [
        {
          title: 'Your app asks for a session',
          text: 'Your backend tells us a customer it has already logged in wants to use the gold screens. It sends a reference of your own invention and nothing else about them.',
        },
        {
          title: 'The customer sees a live rate',
          text: 'A buy rate and a sell rate per gram, both built out from the vendor two-sided price with your commission already applied. There is no single price and no mid.',
        },
        {
          title: 'They enter cash, not grams',
          text: 'A customer types five hundred dirhams and sees the grams it buys. Customers think in money, and showing grams first is where the complaints start.',
        },
        {
          title: 'We hold a firm quote',
          text: 'The rate on screen is held for a set number of seconds — thirty by default. Confirm inside the window and that is the rate. Miss it and they are shown a fresh rate rather than charged an old one.',
        },
        {
          title: 'The order executes against that quote',
          text: 'One quote can only be consumed once. A retried request carrying the same idempotency key returns the original order instead of buying twice.',
        },
        {
          title: 'Settlement writes a balanced set of entries',
          text: 'Metal in, cash out, fee and commission each to their own account. Every order writes rows that sum to zero in both metal and cash, or it does not commit.',
        },
      ],
    },

    { kind: 'heading', text: 'What one settled order leaves behind' },
    {
      kind: 'flow',
      accent: 'teal',
      steps: [
        { title: 'Order record', sub: 'Status, quote, amounts' },
        { title: 'Ledger entries', sub: 'Balanced, double entry' },
        { title: 'Holding updated', sub: 'Weight and average cost' },
        { title: 'Stream frame', sub: 'Every live surface re-reads' },
        { title: 'Webhook', sub: 'Your backend, durably' },
      ],
    },
    {
      kind: 'prose',
      text: 'The stream is a hint and the webhook is the durable channel. A frame can arrive twice or not at all, so a surface re-reads authoritative state rather than patching itself from what the frame carried.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'Rounding is fixed, and always in the same direction' },
    {
      kind: 'table',
      columns: ['Quantity', 'Direction', 'Why'],
      rows: [
        ['Grams credited on a buy', 'Down', 'Never hand out metal nobody paid for'],
        ['Cash charged on a buy', 'Up', 'Never under-collect on a purchase'],
        ['Cash paid out on a sell', 'Down', 'Never over-pay on a sale'],
        ['The customer buy rate', 'Up, at six decimal places', 'The rate can never land below the price we must source at'],
        ['The customer sell rate', 'Down, at six decimal places', 'The rate can never land above what the metal realises'],
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      title: 'The residue is visible, not absorbed',
      text: 'The fractions left by those roundings land on the platform side of the ledger, where they are queryable and reconcilable, rather than disappearing into a difference nobody can account for.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'Selling asks for more than looking does' },
    {
      kind: 'split',
      left: {
        title: 'Reading is cheap',
        body: [
          {
            kind: 'list',
            items: [
              'Rates, portfolio and order history need only a live session.',
              'A customer can look for as long as their session lasts.',
              'A session is idle-limited to fifteen minutes and capped at twelve hours in total by default.',
            ],
          },
        ],
      },
      right: {
        title: 'Moving value is not',
        body: [
          {
            kind: 'list',
            items: [
              'Selling requires a recent confirmation of identity, not just a live session.',
              'On a phone that can be a fingerprint or face check before the sale is signed.',
              'Physical delivery and cash payout carry the same requirement, when they are built.',
              'Whether that check is demanded is yours to switch on.',
            ],
          },
        ],
      },
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'When the market is closed', id: 'market-closed' },
    {
      kind: 'prose',
      text: 'Metal trades on a market with opening hours. It shuts at weekends and on market holidays, and it can pause during the day. This is a customer-facing behaviour that needs a sentence in your product and an answer for your support desk.',
    },
    {
      kind: 'table',
      columns: ['While the market is shut', 'What happens'],
      rows: [
        ['Looking at the rate', 'Works. The customer sees the last rate before the close, with the time it was set.'],
        ['The portfolio', 'Works. Holdings are valued at that same last rate.'],
        ['Order history', 'Works. Nothing about history depends on the market.'],
        ['Buying', 'Refused immediately and by name. Nothing is charged and nothing is reserved.'],
        ['Selling', 'Refused the same way.'],
        ['An order confirmed just before the close', 'Completes normally. Nobody is left half-way through a trade.'],
      ],
    },
    {
      kind: 'callout',
      tone: 'warn',
      title: 'Your app finds out by trying, not by asking',
      text: 'There is no endpoint that reports whether the market is open. The refusal arrives when a customer acts, and it is a distinct, explainable answer rather than a generic failure. If you want the screen to say so up front, your app has to probe — which is a workaround, and worth knowing before a designer assumes an open-or-closed indicator exists.',
    },
    {
      kind: 'prose',
      text: 'Two things stay yours here. The wording a customer sees when trading is shut, including whether it names a reopening time — we publish no trading calendar, so any times shown are yours to state and keep correct. And the support answer to why the price has not moved since Friday.',
    },

    { kind: 'divider' },
    {
      kind: 'cards',
      columns: 3,
      items: [
        {
          title: 'Pricing and commission',
          text: 'How the buy and sell rates on that screen are built, and which part of them is yours.',
          href: '/business/pricing-and-commission',
          accent: 'amber',
        },
        {
          title: 'Surfaces',
          text: 'Where those screens can run, and what changes between a browser and a phone.',
          href: '/business/surfaces',
          accent: 'blue',
        },
        {
          title: 'Operations',
          text: 'What your own staff and your own systems see once trades are flowing.',
          href: '/business/operations',
          accent: 'teal',
        },
      ],
    },
  ],
};

export default page;
